/**
 * /youtube/methods — the "how this works" explainer for the YouTube (Agent 1)
 * pages, mirroring SalienceMethods so both modules read as one portal. Written
 * for a casual user: what the pages show, how comments are collected, how the AI
 * tagging works, what a digest is, and an honest prototype note.
 *
 * Light reading chrome (not the terminal register). House style: no em-dashes.
 */
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PrototypeNote } from "../components/youtube/explainers";

export function YouTubeMethods() {
  return (
    <div className="mx-auto max-w-3xl pb-16">
      <header className="mb-8">
        <Link
          to="/youtube"
          className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-ink-muted hover:text-ink"
        >
          <ArrowLeft size={14} /> YouTube audience
        </Link>
        <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
          YouTube audience · How it works
        </div>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-ink">
          How this works, and how to read it
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-ink-muted">
          A plain-English tour of where the numbers come from, how the AI reads
          comments, and what to trust. No prior knowledge assumed.
        </p>
      </header>

      {/* What this is */}
      <Section title="What this is">
        <p>
          These pages turn the comments on Gary's YouTube videos into something
          you can actually read. Gary's videos attract thousands of comments, far
          too many to go through by hand, so we collect them automatically, have
          an AI tag and summarise them, and present the result as charts, filters,
          and short per-video summaries.
        </p>
        <p>
          The goal is to let Gary and the team see the shape of the conversation
          at a glance: how supportive it is, what people are asking for, what is
          worth a closer look, and how that changes over time.
        </p>
      </Section>

      {/* How comments are collected */}
      <Section title="How the comments are collected">
        <p>
          We regularly pull in the latest comments from every video (roughly once
          an hour) using YouTube's official data feed, and keep our copy up to
          date as people like and reply. We also track view and like counts over
          time, and, where available, the video transcript, which helps the AI
          understand what a comment is responding to.
        </p>
        <p>
          Nothing is scraped or private: it is all the public comment data anyone
          can see on the videos.
        </p>
      </Section>

      {/* How the AI tags */}
      <Section title="How the AI reads each comment">
        <p>
          Once collected, an AI reads each comment and adds a few labels. These
          are the AI's best judgement, not verified facts, and it can get them
          wrong.
        </p>
        <div className="mt-3 space-y-3">
          <TagCard name="Sentiment (tone)">
            Whether the comment is supportive, unsupportive, or neutral toward
            Gary and his content. This is about the position it takes, not just
            whether the language sounds nice, so a blunt comment can still be
            supportive.
          </TagCard>
          <TagCard name="What the audience is doing">
            Whether someone is asking a question, pushing back on a point, sharing
            a personal story, or requesting content. A comment can do several of
            these at once, so these counts can add up to more than the number of
            comments.
          </TagCard>
          <TagCard name="Topics">
            A short, curated list of themes we tag comments against. Most comments
            get no topic. Only the ones clearly about a tracked theme, so this
            highlights what the audience is specifically discussing.
          </TagCard>
          <TagCard name="Safety flags">
            Comments that may be abusive, threatening, or spam. The bar is
            deliberately high on a big public channel, and the AI can be wrong in
            both directions, so a flag is a prompt to look, not a final verdict.
          </TagCard>
        </div>
      </Section>

      {/* Digests */}
      <Section title="What a digest is">
        <p>
          A digest is a short, written summary of what a single video's comments
          are saying: the main questions, the pushback, the stories, and anything
          worth a closer look. An AI writes it from the tagged comments so the
          team can read one page instead of thousands of comments.
        </p>
        <p>
          Digests are produced on a regular schedule, and a fresh version is
          written automatically once a video gathers enough new comments to be
          worth re-reading. That means there is a lag: a brand-new comment can
          take a day or two to be tagged, and a new video may not have a digest
          for a little while.
        </p>
      </Section>

      {/* How to use it */}
      <Section title="How to use these pages">
        <ul className="space-y-2">
          <li>
            <strong className="text-ink">Channel snapshot</strong> is the
            big-picture view: overall tone, what the audience is doing, and the
            most-discussed topics across all videos.
          </li>
          <li>
            <strong className="text-ink">Comments</strong> lets you filter. Click
            any tone, tag, or topic to narrow the list, and combine them (they
            stack together). You can also focus on a single video.
          </li>
          <li>
            <strong className="text-ink">Each video's page</strong> shows its
            digest, its comment breakdown, and how its views and likes have grown.
          </li>
        </ul>
      </Section>

      {/* Prototype honesty */}
      <Section title="This is a prototype">
        <PrototypeNote tone="light" variant="full" />
      </Section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-10">
      <h2 className="mb-3 text-lg font-bold tracking-tight text-ink">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-ink-muted">
        {children}
      </div>
    </section>
  );
}

function TagCard({
  name,
  children,
}: {
  name: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-rule bg-surface p-4">
      <div className="mb-1 text-sm font-bold text-ink">{name}</div>
      <p className="text-sm leading-relaxed text-ink-muted">{children}</p>
    </div>
  );
}
